'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { locales, type Locale } from '@/i18n/config';
import { cn } from '@/lib/cn';

type LanguageSwitcherProps = {
  locale: Locale;
  label: string;
  className?: string;
  size?: 'sm' | 'md';
};

export function LanguageSwitcher({ locale, label, className, size = 'sm' }: LanguageSwitcherProps) {
  const pathname = usePathname();
  const rest = pathname.split('/').slice(2).join('/');

  return (
    <div
      role="group"
      aria-label={label}
      className={cn('flex rounded-full border border-line bg-surface/70 p-[3px]', className)}
    >
      {locales.map((code) => {
        const active = code === locale;
        return (
          <Link
            key={code}
            href={`/${code}${rest ? `/${rest}` : ''}`}
            hrefLang={code}
            aria-current={active ? 'true' : undefined}
            className={cn(
              'inline-flex items-center justify-center rounded-full text-xs font-semibold tracking-[0.06em] transition-colors duration-250',
              size === 'sm' ? 'min-h-9 min-w-11 px-3' : 'min-h-10 min-w-12 px-3.5',
              active ? 'bg-ink text-canvas' : 'text-muted hover:text-ink',
            )}
          >
            {code.toUpperCase()}
          </Link>
        );
      })}
    </div>
  );
}
